/**
 * La vitrina.
 *
 * Diferencia central con la v1: acá el filtrado, la búsqueda, el orden y la
 * paginación los resuelve el SERVIDOR. En la v1 el navegador recibía el
 * catálogo completo y lo filtraba en memoria, lo que funcionaba con 79
 * productos y no escala; además el orden por precio era imposible de calcular
 * en el cliente sin publicarle el margen, que es exactamente la fuga que se
 * cerró. Lo que se conserva íntegro es la estructura: barra lateral de 218 px,
 * grilla fluida, toggle grilla↔tabla y la distinción entre "no hay catálogo" y
 * "los filtros no calzan".
 *
 * A esa distinción se le agrega un tercer comportamiento: cuando la búsqueda
 * deja la lista en cero, el estado vacío OFRECE registrar la demanda con el
 * texto que la persona ya escribió. Antes ese caso era el final del camino y la
 * demanda no quedaba en ninguna parte, así que qué agregar al catálogo se
 * decidía de memoria.
 *
 * LA BÚSQUEDA ENTRA POR LA URL. `?q=…` fija el texto inicial, que es lo que
 * permite que el buscador de la portada termine acá con la búsqueda ya
 * aplicada, y de paso hace que un resultado se pueda compartir por enlace. Los
 * demás filtros siguen siendo estado de la pantalla: son casillas que se prueban
 * y se descartan, y ensuciar la barra de direcciones con cada clic convertiría
 * el botón de atrás en un deshacer de filtros que nadie pidió.
 *
 * Esta pantalla ya no tiene modo «portada». Lo tuvo mientras la página de
 * entrada era la grilla con una banda oscura arriba; ahora la entrada es el
 * buscador de `Portada` y aquel modo quedó sin montar por nadie.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ORDENES_CATALOGO,
  type ItemCatalogo,
  type OrdenCatalogo,
} from '@compranorte/contracts'
import { useCatalogo, useCategorias } from '../api/hooks'
import { useProveedoresAdmin } from '../api/hooksAdmin'
import { useSesion } from '../auth/sesion'
import { agregar, useCarrito, useOlvidarFaenaInvalida } from '../carrito/estado'
import { PedirAlCatalogo } from '../componentes/PedirAlCatalogo'
import { ProductoTile } from '../componentes/ProductoTile'
import { Aviso, Money, Vacio } from '../componentes/ui'
import { IconoBuscar, IconoCarrito, IconoMas, IconoPin } from '../componentes/icons'
import { kg, num } from '../lib/format'

/** Rótulo de cada orden. El valor es el que acepta el backend. */
const ROTULO_ORDEN: Record<OrdenCatalogo, string> = {
  relevancia: 'Orden: catálogo',
  precio_asc: 'Precio: menor a mayor',
  precio_desc: 'Precio: mayor a menor',
  nombre: 'Nombre A-Z',
}

/** Espera antes de mandar la búsqueda: una tecla no es una consulta. */
const ESPERA_BUSQUEDA = 300

export function Catalogo() {
  const { yo, puedeComprar, esEquipo, esOperativo } = useSesion()
  const navigate = useNavigate()
  const carrito = useCarrito()
  const [parametros] = useSearchParams()

  /*
   * El texto de la URL es el VALOR INICIAL, no una atadura permanente: desde acá
   * la caja de búsqueda es estado de la pantalla y se puede seguir escribiendo
   * sin que cada tecla escriba en el historial. El efecto de abajo la vuelve a
   * sincronizar solo si el parámetro cambia, que es lo que ocurre al llegar
   * desde la portada con otra búsqueda.
   */
  const qUrl = parametros.get('q') ?? ''
  const [q, setQ] = useState(qUrl)
  const [qLento, setQLento] = useState('')
  const [categoria, setCategoria] = useState('')
  const [marca, setMarca] = useState('')
  const [proveedorId, setProveedorId] = useState('')
  const [orden, setOrden] = useState<OrdenCatalogo>('relevancia')
  const [vista, setVista] = useState<'grilla' | 'tabla'>('grilla')
  /**
   * Si se desplegó el formulario de «personalizado» desde la tarjeta.
   *
   * Vive acá y no dentro de `PedirAlCatalogo` porque la tarjeta que lo abre está
   * DENTRO de la grilla y el formulario va DEBAJO: son dos lugares del árbol y
   * necesitan compartir el estado. Un formulario embutido en una celda de 228 px
   * quedaría con los campos apilados en una columna angosta.
   */
  const [personalizado, setPersonalizado] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setQLento(q.trim()), ESPERA_BUSQUEDA)
    return () => clearTimeout(t)
  }, [q])

  /*
   * Llegar con otra búsqueda en la URL reemplaza lo que hubiera escrito.
   *
   * Sin esto, ir de la portada al catálogo con «guantes», volver y buscar
   * «filtros» dejaría la pantalla mostrando guantes: React Router reutiliza el
   * componente montado y el estado inicial ya se consumió.
   */
  useEffect(() => {
    setQ(qUrl)
  }, [qUrl])

  /*
   * Cambiar de búsqueda cierra el formulario de personalizado. Es otra demanda:
   * dejarlo abierto con lo anterior escrito invita a mandar una solicitud de
   * "teclado" a quien ya se fue a buscar "monitor".
   */
  useEffect(() => {
    setPersonalizado(false)
  }, [qLento])

  const categorias = useCategorias()

  /*
   * La faena en contexto solo fija la zona con la que el servidor calcula el
   * plazo mostrado. Se manda únicamente con sesión: un anónimo que pase una
   * faena recibe 404 y se quedaría sin catálogo por un dato accesorio.
   */
  const faenaId = yo ? carrito.faenaId : null

  const consulta = useCatalogo({
    ...(categoria ? { categoria } : {}),
    ...(marca ? { marca } : {}),
    ...(proveedorId ? { proveedorId } : {}),
    ...(qLento ? { q: qLento } : {}),
    orden,
    ...(faenaId ? { faenaId } : {}),
  })

  useOlvidarFaenaInvalida(consulta.error, faenaId)

  const items = useMemo<ItemCatalogo[]>(
    () => consulta.data?.pages.flatMap((p) => p.datos) ?? [],
    [consulta.data],
  )

  /*
   * Marcas y proveedores se derivan de lo YA CARGADO, no de un endpoint: la API
   * no publica un catálogo de marcas, y pedir el catálogo entero para llenar un
   * select sería volver al defecto de la v1. El texto de ayuda lo dice, para que
   * nadie lea la lista como exhaustiva.
   */
  const marcas = useMemo(
    () => [...new Set(items.map((p) => p.marca))].sort((a, b) => a.localeCompare(b, 'es')),
    [items],
  )
  /*
   * La lista de proveedores YA NO se deriva de los ítems: el catálogo publica
   * artículos y la identidad del distribuidor no viaja en ellos por diseño.
   *
   * El filtro sigue existiendo porque le sirve al equipo para revisar qué
   * publicó cada uno, pero ahora la lista viene del endpoint de administración
   * y la consulta solo se dispara para el equipo. Para un cliente el filtro no
   * existe: en su vitrina el proveedor no es una dimensión.
   */
  const proveedoresAdmin = useProveedoresAdmin({ estado: 'aprobado' }, esEquipo)
  const proveedores = useMemo(
    () =>
      (proveedoresAdmin.data?.pages.flatMap((p) => p.datos) ?? [])
        .map((p) => [p.id, p.razonSocial] as const)
        .sort((a, b) => a[1].localeCompare(b[1], 'es')),
    [proveedoresAdmin.data],
  )

  const hayFiltros = Boolean(qLento || categoria || marca || proveedorId)

  /**
   * Si se ofrece pedir algo «personalizado» junto a los resultados.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * SOLO CON UNA BÚSQUEDA ESCRITA, y esa condición es el diseño entero.
   *
   * Quien busca "teclado" y encuentra tres que no le sirven —necesita uno
   * lavable de silicona— tenía la búsqueda «funcionando» y ninguna forma de
   * decirlo: la tarjeta de pedir aparecía solo con CERO resultados, así que la
   * demanda más valiosa, la específica, se perdía entera.
   *
   * Sin texto no se ofrece: «pídanos algo personalizado» sobre el catálogo
   * completo no tiene de qué agarrarse, y el formulario nacería vacío. Con una
   * categoría marcada tampoco basta — filtrar por EPP no dice qué le falta a
   * nadie. Lo que hace útil a esta tarjeta es que ya viene precargada con lo que
   * la persona escribió.
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Y solo a quien PUEDE COMPRAR: un anónimo no tiene a quién atribuirle la
   * solicitud —el backend la rechaza con 401— y un proveedor no registra
   * demanda. Es una cortesía de la interfaz; el control es del servidor.
   */
  const ofrecerPersonalizado = puedeComprar && qLento !== ''
  function limpiar() {
    setQ('')
    setQLento('')
    setCategoria('')
    setMarca('')
    setProveedorId('')
  }

  /*
   * "No hay catálogo" y "los filtros no calzan" son cosas distintas, y ofrecer
   * "limpiar filtros" cuando no hay ni un producto manda a la persona a pelear
   * con una barra lateral que no tiene la culpa. La señal es del servidor: la
   * suma de los contadores de categoría, que ya viene con el filtro de
   * visibilidad aplicado.
   */
  const totalPublicado = categorias.data?.reduce((s, c) => s + c.cuenta, 0) ?? null
  const catalogoVacio = totalPublicado === 0

  function alAgregar(productoId: string) {
    /*
     * Primero se guarda, después se redirige. Al revés —que es como estaba— la
     * elección de quien todavía no entró se perdía entre el clic y el login, y
     * la persona llegaba a un carrito vacío sin saber por qué.
     *
     * Mirar el catálogo no exige identidad y elegir tampoco: el carrito vive en
     * `localStorage` y solo guarda el artículo y la cantidad. La identidad se
     * exige al cotizar y al emitir, que es donde el servidor pone precios y
     * reserva stock.
     */
    agregar(productoId, 1)
    if (!yo) navigate('/entrar', { state: { volverA: '/carrito' } })
  }

  const enCarrito = useMemo(
    () => new Map(carrito.items.map((i) => [i.articuloId, i.cantidad])),
    [carrito.items],
  )

  const contenido = (
    <div className="grid lg:grid-cols-[218px_1fr] gap-4 items-start">
      <h1 className="sr-only">Catálogo de insumos</h1>

      <aside className="panel lg:sticky lg:top-12">
        <div className="panel-head">
          <span className="panel-title">Filtros</span>
          {hayFiltros && (
            <button type="button" className="btn btn-fantasma btn-xs" onClick={limpiar}>
              Limpiar
            </button>
          )}
        </div>
        <div className="p-3 space-y-3">
          <div>
            <span className="etiqueta">Categoría</span>
            {categorias.isPending ? (
              <p className="text-[13px] text-acero-500">Cargando…</p>
            ) : (
              <div className="space-y-0.5">
                <BotonFiltro
                  activo={categoria === ''}
                  onClick={() => setCategoria('')}
                  nombre="Todas"
                  cuenta={totalPublicado}
                />
                {(categorias.data ?? [])
                  .filter((c) => c.cuenta > 0)
                  .map((c) => (
                    <BotonFiltro
                      key={c.id}
                      activo={categoria === c.slug}
                      onClick={() => setCategoria(c.slug)}
                      nombre={c.nombre}
                      cuenta={c.cuenta}
                    />
                  ))}
              </div>
            )}
          </div>

          <label className="block">
            <span className="etiqueta">Marca</span>
            <select className="campo" value={marca} onChange={(e) => setMarca(e.target.value)}>
              <option value="">Todas</option>
              {marcas.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <p className="ayuda">Marcas de los productos ya cargados en esta lista.</p>
          </label>

          {/*
            SOLO PARA EL EQUIPO. La identidad del distribuidor no se le muestra a
            un cliente en ninguna parte del catálogo, así que tampoco como
            dimensión de filtrado: un select con cuatro razones sociales sería la
            fuga más obvia de todas.
          */}
          {esEquipo && (
            <label className="block">
              <span className="etiqueta">Proveedor</span>
              <select
                className="campo"
                value={proveedorId}
                onChange={(e) => setProveedorId(e.target.value)}
              >
                <option value="">Todos</option>
                {proveedores.map(([id, razon]) => (
                  <option key={id} value={id}>
                    {razon}
                  </option>
                ))}
              </select>
            </label>
          )}

        </div>
      </aside>

      <div className="min-w-0 space-y-3">
        <div className="panel p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <IconoBuscar
              size={15}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-acero-400"
            />
            <input
              className="campo pl-7"
              type="search"
              aria-label="Buscar en el catálogo"
              placeholder="Buscar por nombre, SKU, marca o descripción…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            className="campo w-auto"
            aria-label="Ordenar resultados"
            value={orden}
            onChange={(e) => setOrden(e.target.value as OrdenCatalogo)}
          >
            {ORDENES_CATALOGO.map((o) => (
              <option key={o} value={o}>
                {ROTULO_ORDEN[o]}
              </option>
            ))}
          </select>
          <div
            className="flex rounded-sm border border-acero-300 overflow-hidden"
            role="group"
            aria-label="Forma de ver el catálogo"
          >
            {(['grilla', 'tabla'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVista(v)}
                aria-pressed={vista === v}
                className={`px-2.5 py-1.5 text-[12px] ${
                  vista === v ? 'bg-acero-900 text-white' : 'bg-white hover:bg-acero-50'
                }`}
              >
                {v === 'grilla' ? 'Grilla' : 'Tabla'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 text-[12px] text-acero-600 px-0.5 flex-wrap">
          {/*
            El conteo dice "24 de 68" cuando hay más páginas, no "24 productos
            cargados": junto al "68 productos publicados" de la banda superior,
            un 24 suelto se lee como si el catálogo tuviera 24 productos. Un
            número que se puede malinterpretar es peor que no ponerlo.

            Cuando hay filtros activos el total no aplica —68 es el catálogo
            completo, no el filtrado—, así que ahí se omite.
          */}
          <span>
            <strong className="num text-acero-900">{num(items.length)}</strong>
            {consulta.hasNextPage && totalPublicado !== null && !hayFiltros ? (
              <>
                {' de '}
                <strong className="num text-acero-900">{num(totalPublicado)}</strong>
                {' productos'}
              </>
            ) : (
              ' productos'
            )}{' '}
            · precios netos, IVA se agrega en el resumen
          </span>
          <span className="inline-flex items-center gap-1">
            <IconoPin size={13} />
            {faenaId
              ? 'Plazos estimados a su faena de destino'
              : 'El plazo definitivo sale al elegir la faena en el carrito'}
          </span>
        </div>

        {consulta.isError && (
          <Aviso tono="error" titulo="No se pudo cargar el catálogo">
            <span role="alert">{consulta.error.message}</span>
          </Aviso>
        )}

        {consulta.isPending ? (
          <p className="text-[13px] text-acero-500 py-6">Cargando…</p>
        ) : items.length === 0 && catalogoVacio ? (
          <Vacio
            titulo="Todavía no hay productos publicados"
            detalle="Estamos homologando a los primeros proveedores. Si vende insumos de faena y quiere publicar acá, escríbanos: revisamos su catálogo y lo damos de alta."
            accion={
              <Link to="/registro" className="btn btn-secundario">
                Registrarme como proveedor
              </Link>
            }
          />
        ) : items.length === 0 ? (
          /*
            CERO RESULTADOS CON FILTROS. Antes esto era el final del camino: la
            tarjeta decía "escríbanos" y no había a dónde escribir, así que la
            demanda se perdía y la decisión de qué agregar al catálogo se seguía
            tomando de memoria. Ahora el mismo estado ofrece registrarla, con el
            texto que la persona ya escribió.

            Solo a quien PUEDE COMPRAR: un anónimo no tiene a quién atribuirle la
            solicitud —el backend la rechazaría con 401— y un proveedor no
            registra demanda. Esconder el botón es una cortesía, no el control:
            el control es `exigirAutenticado` del lado del servidor.
          */
          <div className="space-y-3">
            <Vacio
              titulo="No hay productos con esos filtros"
              detalle={
                puedeComprar
                  ? 'Pruebe con menos filtros o busque por SKU. Y si el insumo no está en el catálogo, pídanoslo: lo revisamos con nuestros proveedores.'
                  : 'Pruebe con menos filtros o busque por SKU. Si el insumo que necesita no está en el catálogo, lo podemos incorporar: escríbanos y lo homologamos con el proveedor.'
              }
              accion={
                <button type="button" className="btn btn-secundario" onClick={limpiar}>
                  Limpiar filtros
                </button>
              }
            />
            {puedeComprar && <PedirAlCatalogo textoBuscado={qLento} faenaId={faenaId} />}
          </div>
        ) : vista === 'grilla' ? (
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(228px,1fr))]">
            {items.map((p) => (
              <TarjetaProducto
                key={p.id}
                producto={p}
                conSesion={Boolean(yo)}
                puedeComprar={puedeComprar && esOperativo}
                enCarrito={enCarrito.get(p.id) ?? 0}
                onAgregar={() => alAgregar(p.id)}
              />
            ))}
            {ofrecerPersonalizado && (
              <TarjetaPersonalizado
                texto={qLento}
                abierto={personalizado}
                onAbrir={() => setPersonalizado(true)}
              />
            )}
          </div>
        ) : (
          <div className="panel scroll-x">
            <table className="tabla">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Producto</th>
                  <th>Categoría</th>
                  <th>Origen</th>
                  <th className="th-num">Peso</th>
                  <th className="th-num">Stock</th>
                  <th className="th-num">Entrega</th>
                  <th className="th-num">Precio neto</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id}>
                    <td className="num text-[11.5px] text-acero-600">{p.codigo}</td>
                    <td className="min-w-[220px]">
                      <Link
                        to={`/producto/${encodeURIComponent(p.id)}`}
                        className="font-medium hover:text-marca-600"
                      >
                        {p.nombre}
                      </Link>
                      <div className="text-[11px] text-acero-500">{p.marca}</div>
                    </td>
                    <td className="text-[12px] text-acero-600 whitespace-nowrap">
                      {p.categoriaNombre}
                    </td>
                    <td className="text-[12px] whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {/*
                          Ciudad de origen, nunca el distribuidor. La ciudad se
                          publica porque de ella dependen el plazo prometido y el
                          agrupamiento del flete, y hay cinco posibles: no
                          identifica a nadie.
                        */}
                        <span>{p.ciudadOrigen}</span>
                      </div>
                      {/* Cuántas ofertas hay detrás es dato del equipo. */}
                      {p.ofertas !== undefined && (
                        <div className="text-[11px] text-acero-500">
                          {p.ofertas === 1 ? '1 oferta' : `${p.ofertas} ofertas`}
                        </div>
                      )}
                    </td>
                    <td className="td-num num">{kg(p.pesoKg)}</td>
                    <td className="td-num num">
                      {p.stockDisponible !== undefined
                        ? num(p.stockDisponible)
                        : p.hayStock
                          ? 'sí'
                          : 'no'}
                    </td>
                    <td className="td-num num">{yo ? `${num(p.diasEntrega)} d` : '—'}</td>
                    <td className="td-num font-semibold">
                      <Money v={p.precioVentaNeto} />
                    </td>
                    <td className="td-num">
                      {/* Un lector ve el precio y el stock; no el botón de comprar. */}
                      {((puedeComprar && esOperativo) || !yo) && (
                        <button
                          type="button"
                          className="btn btn-secundario btn-xs"
                          onClick={() => alAgregar(p.id)}
                          disabled={!p.hayStock}
                        >
                          {yo ? 'Agregar' : 'Entrar'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {/*
                  El equivalente de la tarjeta, en la vista de tabla. La misma
                  oferta tiene que existir en las DOS vistas: quien prefiere la
                  tabla no es quien tiene menos que pedir, y dejarla solo en la
                  grilla haría que la opción apareciera y desapareciera al pulsar
                  un conmutador que no habla de esto.
                */}
                {ofrecerPersonalizado && (
                  <tr>
                    <td colSpan={9} className="bg-acero-50">
                      <button
                        type="button"
                        onClick={() => setPersonalizado(true)}
                        disabled={personalizado}
                        className="flex w-full items-center gap-2 py-1 text-left text-[12.5px]
                          text-acero-700 disabled:cursor-default"
                      >
                        <IconoMas size={14} className="shrink-0 text-acero-500" />
                        <span>
                          ¿Ninguno sirve? Pídanos el{' '}
                          <strong className="text-acero-900">{qLento}</strong> que necesita.
                        </span>
                        {!personalizado && (
                          <span className="btn btn-secundario btn-xs ml-auto shrink-0">
                            Pedir uno a medida
                          </span>
                        )}
                        {personalizado && (
                          <span className="ml-auto shrink-0 text-[11px] text-marca-600">
                            formulario abajo ↓
                          </span>
                        )}
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/*
          El formulario va DEBAJO de los resultados, no dentro de la tarjeta.
          Una celda de 228 px dejaría los campos apilados en una columna angosta,
          y en la tabla no hay dónde ponerlo sin romper las columnas. La tarjeta
          señala «formulario abajo ↓» para que el salto no sea una sorpresa.

          `iniciarAbierto` porque el clic de abrir YA ocurrió sobre la tarjeta:
          volver a pedirlo sobre un botón «Pídanoslo» idéntico sería hacer el
          mismo gesto dos veces para llegar al mismo sitio.

          `items.length > 0` evita duplicarlo: el estado vacío tiene su propia
          copia del formulario, y los resultados pueden caer a cero por un filtro
          de la barra lateral sin que el texto buscado cambie —que es lo único
          que reinicia `personalizado`—.
        */}
        {ofrecerPersonalizado && personalizado && items.length > 0 && (
          <PedirAlCatalogo textoBuscado={qLento} faenaId={faenaId} iniciarAbierto />
        )}

        {/*
          "Ver más" con el cursor, no scroll infinito: con scroll infinito el pie
          de página es inalcanzable y volver atrás desde una ficha reinicia la
          lista en la primera página.
        */}
        {consulta.hasNextPage && (
          <div className="text-center pt-1">
            <button
              type="button"
              className="btn btn-secundario"
              onClick={() => void consulta.fetchNextPage()}
              disabled={consulta.isFetchingNextPage}
            >
              {consulta.isFetchingNextPage ? 'Cargando…' : 'Ver más productos'}
            </button>
          </div>
        )}
      </div>
    </div>
  )

  return contenido
}

/* ------------------------------------------------------------------------- */

function BotonFiltro({
  activo,
  onClick,
  nombre,
  cuenta,
}: {
  activo: boolean
  onClick: () => void
  nombre: string
  cuenta: number | null
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={`w-full text-left px-1.5 py-1 rounded-sm text-[12.5px] flex justify-between gap-1 ${
        activo ? 'bg-acero-900 text-white' : 'hover:bg-acero-100'
      }`}
    >
      <span className="truncate">{nombre}</span>
      {cuenta !== null && <span className="num opacity-70">{num(cuenta)}</span>}
    </button>
  )
}

/**
 * La tarjeta «personalizado», junto a los resultados de la búsqueda.
 *
 * Es una tarjeta más de la grilla y no un banner arriba, y ahí está la idea: se
 * lee como una opción del resultado —«ninguno de estos tres, quiero uno
 * distinto»— en vez de como un aviso que se saltea. Por eso comparte la caja de
 * `TarjetaProducto` y se distingue solo por el borde punteado y la ausencia de
 * precio: se parece lo suficiente para leerse en la misma pasada y lo bastante
 * poco para no confundirse con algo que se pueda agregar al carrito.
 *
 * Va AL FINAL de la grilla. Con tres resultados queda literalmente al lado, que
 * es el caso que la motiva; con cuarenta queda abajo, después de que la persona
 * confirmó que ninguno servía — que es cuando la necesita.
 */
function TarjetaPersonalizado({
  texto,
  abierto,
  onAbrir,
}: {
  texto: string
  abierto: boolean
  onAbrir: () => void
}) {
  return (
    <article
      className={`panel flex flex-col border-dashed ${
        abierto ? 'border-marca-500 bg-marca-700/5' : 'hover:border-acero-400'
      } transition-colors`}
    >
      <button
        type="button"
        onClick={onAbrir}
        disabled={abierto}
        className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center
          disabled:cursor-default"
      >
        <span
          className="inline-flex h-9 w-9 items-center justify-center rounded-sm border
            border-dashed border-acero-300 text-acero-500"
        >
          <IconoMas size={18} />
        </span>
        <span className="text-[13px] font-semibold text-acero-900">Personalizado</span>
        <span className="text-[11.5px] leading-snug text-acero-600">
          {/*
            Se nombra lo que la persona buscó: es lo que convierte una invitación
            genérica en una que habla de lo suyo. Truncado, porque una búsqueda
            larga desarmaría la tarjeta.
          */}
          ¿Ninguno de estos? Pídanos el <strong className="text-acero-900">{texto}</strong> que
          necesita y lo buscamos con nuestros proveedores.
        </span>
        {!abierto && <span className="btn btn-secundario btn-xs mt-0.5">Pedir uno a medida</span>}
        {abierto && <span className="text-[11px] text-marca-600">formulario abajo ↓</span>}
      </button>
    </article>
  )
}

function TarjetaProducto({
  producto,
  conSesion,
  puedeComprar,
  enCarrito,
  onAgregar,
}: {
  producto: ItemCatalogo
  conSesion: boolean
  puedeComprar: boolean
  enCarrito: number
  onAgregar: () => void
}) {
  const enlace = `/producto/${encodeURIComponent(producto.id)}`
  /*
   * `ProductoTile` pide la forma del dominio, donde `imagenUrl` es opcional; el
   * catálogo la manda nullable. Se traduce acá, en el borde, en vez de aflojar
   * el tipo del componente compartido.
   */
  const ficha = {
    nombre: producto.nombre,
    sku: producto.codigo,
    marca: producto.marca,
    categoriaId: producto.categoriaId,
    ...(producto.imagenUrl ? { imagenUrl: producto.imagenUrl } : {}),
  }

  return (
    <article className="panel flex flex-col hover:border-acero-400 transition-colors">
      <Link to={enlace}>
        <ProductoTile producto={ficha} />
      </Link>
      <div className="p-2.5 flex flex-col gap-1.5 flex-1">
        <div className="flex items-start justify-between gap-1">
          <span className="text-[10.5px] uppercase tracking-[0.07em] text-acero-500 font-semibold">
            {producto.categoriaNombre}
          </span>
        </div>
        <Link
          to={enlace}
          className="text-[13px] font-semibold leading-snug hover:text-marca-600 line-clamp-2"
        >
          {producto.nombre}
        </Link>
        <div className="num text-[11px] text-acero-500">
          {producto.codigo} · {producto.marca}
        </div>

        <dl className="mt-0.5 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px] text-acero-600 border-t border-acero-100 pt-1.5">
          {/*
            Peso REAL, no facturable: el facturable depende del divisor
            volumétrico, que es un parámetro de negocio que vive en la base y no
            se publica. El flete se calcula sobre el carrito completo de todos
            modos, así que un facturable por unidad calculado acá con un divisor
            adivinado sería una cifra que no cuadra con la que se cobra.
          */}
          <div className="flex justify-between">
            <dt>Peso</dt>
            <dd className="num text-acero-800">{kg(producto.pesoKg)}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Stock</dt>
            <dd className="num text-acero-800">
              {producto.stockDisponible !== undefined
                ? num(producto.stockDisponible)
                : producto.hayStock
                  ? 'disponible'
                  : 'agotado'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt>Origen</dt>
            <dd className="text-acero-800 truncate">{producto.ciudadOrigen}</dd>
          </div>
          {conSesion && (
            <div className="flex justify-between">
              <dt>Entrega</dt>
              <dd className="num text-acero-800">{num(producto.diasEntrega)} d</dd>
            </div>
          )}
        </dl>

        <div className="mt-auto pt-2 flex items-end justify-between gap-2">
          <div>
            <div className="text-[17px] font-semibold leading-none">
              <Money v={producto.precioVentaNeto} />
            </div>
            <div className="text-[10.5px] text-acero-500 mt-0.5">neto por unidad</div>
          </div>
          {(puedeComprar || !conSesion) && (
            <button
              type="button"
              className="btn btn-accion btn-xs"
              onClick={onAgregar}
              disabled={!producto.hayStock}
              title={producto.hayStock ? 'Agregar al carrito' : 'Sin stock disponible'}
            >
              <IconoCarrito size={13} />
              {!conSesion ? 'Entrar' : enCarrito > 0 ? `+1 (${num(enCarrito)})` : 'Agregar'}
            </button>
          )}
        </div>
      </div>
    </article>
  )
}
